import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { getAuthHeaders } from '../context/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { Plus, Search, ToggleLeft, ToggleRight, Trash2, Edit2, X, Check, Loader2, Gift, Users, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { fetchCodes(); }, []);

  const fetchCodes = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/promo-codes`, {
        credentials: 'include', headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.clone().json();
        setCodes(data.promo_codes || []);
      }
    } catch { toast.error('Failed to fetch promo codes'); }
    finally { setLoading(false); }
  };

  const toggleActive = async (code) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/promo-codes/${code.code}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ active: !code.active })
      });
      if (res.ok) {
        setCodes(prev => prev.map(c => c.code === code.code ? { ...c, active: !c.active } : c));
        toast.success(`${code.code} ${code.active ? 'deactivated' : 'activated'}`);
      }
    } catch { toast.error('Failed to update'); }
  };

  const deleteCode = async (codeStr) => {
    setDeleteTarget(codeStr);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/promo-codes/${deleteTarget}`, {
        method: 'DELETE', credentials: 'include', headers: getAuthHeaders()
      });
      if (res.ok) {
        setCodes(prev => prev.filter(c => c.code !== deleteTarget));
        toast.success(`${deleteTarget} deleted`);
      }
    } catch { toast.error('Failed to delete'); }
    setDeleteTarget(null);
  };

  const filtered = codes.filter(c =>
    c.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <AdminLayout>
      <div data-testid="admin-promo-codes">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-medium text-charcoal">Promo Codes</h1>
            <p className="text-sm text-gray-soft mt-1">{codes.length} code{codes.length !== 1 ? 's' : ''} total</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-sage hover:bg-sage-dark" data-testid="create-promo-btn">
            <Plus className="h-4 w-4 mr-1" /> Create Code
          </Button>
        </div>

        {/* Create modal */}
        {showCreate && (
          <CreatePromoModal onClose={() => setShowCreate(false)} onCreated={(newCode) => {
            setCodes(prev => [newCode, ...prev]);
            setShowCreate(false);
          }} />
        )}

        {/* Edit modal */}
        {editingCode && (
          <EditPromoModal code={editingCode} onClose={() => setEditingCode(null)} onUpdated={(updated) => {
            setCodes(prev => prev.map(c => c.code === updated.code ? updated : c));
            setEditingCode(null);
          }} />
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-soft" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search codes..."
            className="w-full pl-10 pr-4 py-2.5 border border-beige rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-sage/50 text-sm"
            data-testid="promo-search-input"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-sage" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-soft">
            <Gift className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>{searchQuery ? 'No matching promo codes' : 'No promo codes yet. Create one!'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(code => (
              <PromoCodeCard
                key={code.code}
                code={code}
                onToggle={() => toggleActive(code)}
                onDelete={() => deleteCode(code.code)}
                onEdit={() => setEditingCode(code)}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Promo Code"
        message={`Delete "${deleteTarget}"? Existing grants will not be affected.`}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        confirmVariant="danger"
        testId="delete-promo-dialog"
      />
    </AdminLayout>
  );
}

function PromoCodeCard({ code, onToggle, onDelete, onEdit, formatDate }) {
  const isExpired = code.ends_at && new Date(code.ends_at) < new Date();
  const hasntStarted = code.starts_at && new Date(code.starts_at) > new Date();
  const maxedOut = code.max_redemptions && code.redemption_count >= code.max_redemptions;

  return (
    <Card className={`${!code.active || isExpired ? 'opacity-60' : ''}`} data-testid={`promo-card-${code.code}`}>
      <CardContent className="py-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Left: Code + description */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-medium text-charcoal text-lg" data-testid={`promo-code-${code.code}`}>
                {code.code}
              </span>
              {code.active && !isExpired && !hasntStarted ? (
                <Badge className="bg-green-100 text-green-700" data-testid={`promo-status-${code.code}`}>Active</Badge>
              ) : isExpired ? (
                <Badge className="bg-red-100 text-red-700">Expired</Badge>
              ) : hasntStarted ? (
                <Badge className="bg-amber-100 text-amber-700">Scheduled</Badge>
              ) : (
                <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>
              )}
              {maxedOut && <Badge className="bg-orange-100 text-orange-700">Maxed Out</Badge>}
            </div>
            {code.description && (
              <p className="text-sm text-gray-soft mt-1">{code.description}</p>
            )}
          </div>

          {/* Middle: Stats */}
          <div className="flex items-center gap-4 text-sm text-gray-soft">
            <div className="flex items-center gap-1" title="Free items granted">
              <Gift className="h-3.5 w-3.5" />
              <span>{code.free_items} items</span>
            </div>
            <div className="flex items-center gap-1" title="Duration">
              <Calendar className="h-3.5 w-3.5" />
              <span>{code.duration_days}d</span>
            </div>
            <div className="flex items-center gap-1" title="Redemptions">
              <Users className="h-3.5 w-3.5" />
              <span>{code.redemption_count || 0}{code.max_redemptions ? `/${code.max_redemptions}` : ''}</span>
            </div>
          </div>

          {/* Date range */}
          <div className="text-xs text-gray-soft min-w-0">
            {(code.starts_at || code.ends_at) && (
              <span>{formatDate(code.starts_at)} — {formatDate(code.ends_at)}</span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-cream transition-colors text-gray-soft hover:text-charcoal"
              title="Edit" data-testid={`edit-promo-${code.code}`}>
              <Edit2 className="h-4 w-4" />
            </button>
            <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-cream transition-colors" title={code.active ? 'Deactivate' : 'Activate'}
              data-testid={`toggle-promo-${code.code}`}>
              {code.active
                ? <ToggleRight className="h-5 w-5 text-sage" />
                : <ToggleLeft className="h-5 w-5 text-gray-400" />
              }
            </button>
            <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-red-400 hover:text-red-600"
              title="Delete" data-testid={`delete-promo-${code.code}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CreatePromoModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    code: '',
    free_items: 10,
    duration_days: 30,
    max_redemptions: '',
    starts_at: '',
    ends_at: '',
    description: '',
    active: true,
  });
  const [submitting, setSubmitting] = useState(false);

  const calcEndDate = (startStr, days) => {
    if (!startStr || !days) return '';
    const d = new Date(startStr);
    d.setDate(d.getDate() + parseInt(days));
    return d.toISOString().slice(0, 16);
  };

  const updateField = (field, value) => {
    setForm(f => {
      const next = { ...f, [field]: value };
      if (field === 'starts_at' || field === 'duration_days') {
        const start = field === 'starts_at' ? value : f.starts_at;
        const days = field === 'duration_days' ? value : f.duration_days;
        next.ends_at = calcEndDate(start, days);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    if (form.free_items < 1) { toast.error('Free items must be at least 1'); return; }

    setSubmitting(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        free_items: parseInt(form.free_items),
        duration_days: parseInt(form.duration_days) || 30,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        description: form.description,
        active: form.active,
      };
      const res = await fetch(`${BACKEND_URL}/api/admin/promo-codes`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.clone().json();
        toast.success(`Promo code ${data.code} created!`);
        onCreated(data);
      } else {
        const err = await res.clone().json();
        toast.error(err.detail || 'Failed to create');
      }
    } catch { toast.error('Failed to create promo code'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/60 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-beige">
          <h2 className="text-lg font-medium text-charcoal">Create Promo Code</h2>
          <button onClick={onClose} className="p-1 hover:bg-cream rounded-lg"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="create-promo-form">
          <div>
            <label className="text-sm font-medium text-charcoal">Code *</label>
            <input type="text" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
              className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50 font-mono"
              placeholder="e.g. SUMMER2026" data-testid="promo-code-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-charcoal">Free Items *</label>
              <input type="number" min="1" value={form.free_items} onChange={e => setForm(f => ({ ...f, free_items: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                data-testid="promo-free-items-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-charcoal">Duration (days)</label>
              <input type="number" min="1" value={form.duration_days} onChange={e => updateField('duration_days', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                data-testid="promo-duration-input" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-charcoal">Max Redemptions</label>
            <input type="number" min="1" value={form.max_redemptions} onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))}
              placeholder="Unlimited" className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
              data-testid="promo-max-redemptions-input" />
            <p className="text-xs text-gray-soft mt-1">Leave empty for unlimited</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-charcoal">Start Date</label>
              <input type="datetime-local" value={form.starts_at} onChange={e => updateField('starts_at', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50 text-sm"
                data-testid="promo-starts-at-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-charcoal">End Date</label>
              <input type="datetime-local" value={form.ends_at} readOnly
                className="w-full mt-1 px-3 py-2 border border-beige rounded-lg bg-gray-50 text-gray-soft text-sm cursor-not-allowed"
                data-testid="promo-ends-at-input" />
              <p className="text-xs text-gray-soft mt-1">Calculated from start + duration</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-charcoal">Description</label>
            <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Internal note about this promo" className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
              data-testid="promo-description-input" />
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setForm(f => ({ ...f, active: !f.active }))}
              className="p-0.5" data-testid="promo-active-toggle">
              {form.active ? <ToggleRight className="h-6 w-6 text-sage" /> : <ToggleLeft className="h-6 w-6 text-gray-400" />}
            </button>
            <span className="text-sm text-charcoal">{form.active ? 'Active immediately' : 'Create as inactive'}</span>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={submitting} className="flex-1 bg-sage hover:bg-sage-dark" data-testid="submit-create-promo-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Create
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPromoModal({ code, onClose, onUpdated }) {
  const toLocal = (d) => {
    if (!d) return '';
    return new Date(d).toISOString().slice(0, 16);
  };

  const [form, setForm] = useState({
    free_items: code.free_items || 10,
    duration_days: code.duration_days || 30,
    max_redemptions: code.max_redemptions || '',
    starts_at: toLocal(code.starts_at),
    ends_at: toLocal(code.ends_at),
    description: code.description || '',
    active: code.active,
  });
  const [submitting, setSubmitting] = useState(false);

  const calcEndDate = (startStr, days) => {
    if (!startStr || !days) return '';
    const d = new Date(startStr);
    d.setDate(d.getDate() + parseInt(days));
    return d.toISOString().slice(0, 16);
  };

  const updateField = (field, value) => {
    setForm(f => {
      const next = { ...f, [field]: value };
      if (field === 'starts_at' || field === 'duration_days') {
        const start = field === 'starts_at' ? value : f.starts_at;
        const days = field === 'duration_days' ? value : f.duration_days;
        next.ends_at = calcEndDate(start, days);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.free_items < 1) { toast.error('Free items must be at least 1'); return; }

    setSubmitting(true);
    try {
      const body = {
        free_items: parseInt(form.free_items),
        duration_days: parseInt(form.duration_days) || 30,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        description: form.description,
        active: form.active,
      };
      const res = await fetch(`${BACKEND_URL}/api/admin/promo-codes/${code.code}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.clone().json();
        toast.success(`${code.code} updated`);
        onUpdated(data);
      } else {
        const err = await res.clone().json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch { toast.error('Failed to update promo code'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/60 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-beige">
          <h2 className="text-lg font-medium text-charcoal">Edit <span className="font-mono">{code.code}</span></h2>
          <button onClick={onClose} className="p-1 hover:bg-cream rounded-lg"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="edit-promo-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-charcoal">Free Items *</label>
              <input type="number" min="1" value={form.free_items} onChange={e => setForm(f => ({ ...f, free_items: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                data-testid="edit-promo-free-items" />
            </div>
            <div>
              <label className="text-sm font-medium text-charcoal">Duration (days)</label>
              <input type="number" min="1" value={form.duration_days} onChange={e => updateField('duration_days', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                data-testid="edit-promo-duration" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-charcoal">Max Redemptions</label>
            <input type="number" min="1" value={form.max_redemptions} onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))}
              placeholder="Unlimited" className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
              data-testid="edit-promo-max-redemptions" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-charcoal">Start Date</label>
              <input type="datetime-local" value={form.starts_at} onChange={e => updateField('starts_at', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50 text-sm"
                data-testid="edit-promo-starts-at" />
            </div>
            <div>
              <label className="text-sm font-medium text-charcoal">End Date</label>
              <input type="datetime-local" value={form.ends_at} readOnly
                className="w-full mt-1 px-3 py-2 border border-beige rounded-lg bg-gray-50 text-gray-soft text-sm cursor-not-allowed"
                data-testid="edit-promo-ends-at" />
              <p className="text-xs text-gray-soft mt-1">Calculated from start + duration</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-charcoal">Description</label>
            <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Internal note" className="w-full mt-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
              data-testid="edit-promo-description" />
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setForm(f => ({ ...f, active: !f.active }))} className="p-0.5" data-testid="edit-promo-active-toggle">
              {form.active ? <ToggleRight className="h-6 w-6 text-sage" /> : <ToggleLeft className="h-6 w-6 text-gray-400" />}
            </button>
            <span className="text-sm text-charcoal">{form.active ? 'Active' : 'Inactive'}</span>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={submitting} className="flex-1 bg-sage hover:bg-sage-dark" data-testid="submit-edit-promo-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
